import React, { useState, useContext, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdOutlineEdit, MdOutlineDelete } from "react-icons/md";
import { toast } from "react-toastify";
import AppContext from "../../context/AppContext";
import {
  listCcAdminRoles,
  getCcAdminRole,
  createCcAdminRole,
  patchCcAdminRole,
  deactivateCcAdminRole,
} from "../../service/CallCenterRoleAdmin";
import { getCallCenterApiErrorMessage } from "../../service/CallCenterApi";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import CcRoleFormModal, { CODE_PATTERN } from "./CcRoleFormModal";
import { getPermissionByScreenCode, canShowActionColumn } from "../../utils/permissions";
import {
  hasAnyScreenPermission,
  permissionsObjectToPayload,
} from "../../utils/ccScreenPermissions";

const SORT_BY_OPTIONS = [
  { value: "CODE", label: "Code" },
  { value: "DISPLAY_NAME", label: "Name" },
  { value: "CREATED_AT", label: "Created" },
];

const SORT_DIR_OPTIONS = [
  { value: "ASC", label: "Ascending" },
  { value: "DESC", label: "Descending" },
];

const RoleManagement = () => {
  const { theme, langMode, userType } = useContext(AppContext);
  const [roleList, setRoleList] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState("CODE");
  const [sortDir, setSortDir] = useState("ASC");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listMeta, setListMeta] = useState({
    hasNext: undefined,
    hasPrevious: undefined,
    apiPageCount: 0,
  });
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingRole, setEditingRole] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = userType === "super_admin";
  const rolePerm = getPermissionByScreenCode("ROLE_MANAGEMENT");
  const showRoleActionColumn = isSuperAdmin || canShowActionColumn("ROLE_MANAGEMENT");
  const canCreateRole = isSuperAdmin || Boolean(rolePerm?.canCreate);
  const canUpdateRole = isSuperAdmin || Boolean(rolePerm?.canUpdate);
  const canDeleteRole = isSuperAdmin || Boolean(rolePerm?.canDelete);

  const apiPageCount =
    typeof listMeta.apiPageCount === "number" && listMeta.apiPageCount > 0
      ? listMeta.apiPageCount
      : 0;
  const lastPageIndex =
    totalValue <= 0
      ? 0
      : apiPageCount > 0
        ? Math.max(0, apiPageCount - 1)
        : Math.max(0, Math.ceil(totalValue / pageSize) - 1);

  const canGoPrev = currentPage > 0;
  const canGoNext =
    listMeta.hasNext === false
      ? false
      : listMeta.hasNext === true
        ? true
        : currentPage < lastPageIndex;

  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const totalItems = totalValue;
  const isRtl = langMode === "ar";

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const data = await listCcAdminRoles({
        page: currentPage,
        size: pageSize,
        sortBy,
        sortDir,
        includeInactive,
      });
      setRoleList(Array.isArray(data?.items) ? data.items : []);
      setTotalValue(typeof data?.totalElements === "number" ? data.totalElements : 0);
      setListMeta({
        hasNext: data?.hasNext,
        hasPrevious: data?.hasPrevious,
        apiPageCount: typeof data?.totalPages === "number" ? data.totalPages : 0,
      });
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, "Failed to load roles")
          : "Failed to load roles"
      );
      setRoleList([]);
      setTotalValue(0);
      setListMeta({ hasNext: undefined, hasPrevious: undefined, apiPageCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [currentPage, pageSize, sortBy, sortDir, includeInactive]);

  const openCreate = () => {
    setFormMode("create");
    setEditingRole(null);
    setFormOpen(true);
  };

  const openEdit = async (role) => {
    try {
      setSaving(true);
      const detail = await getCcAdminRole(role.id);
      setEditingRole(detail);
      setFormMode("edit");
      setFormOpen(true);
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, "Failed to load role")
          : "Failed to load role"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveForm = async (form) => {
    if (!form.displayName?.trim()) {
      toast.warning("Display name is required");
      return;
    }
    if (
      !hasAnyScreenPermission(form.screenPermissions) &&
      (formMode === "create" || !editingRole?.systemRole)
    ) {
      toast.warning("Select at least one screen permission");
      return;
    }

    setSaving(true);
    try {
      if (formMode === "create") {
        const code = String(form.code || "").trim().toUpperCase();
        if (!CODE_PATTERN.test(code)) {
          toast.warning("Role code must be uppercase letters, numbers, and underscores (e.g. TEAM_LEAD)");
          setSaving(false);
          return;
        }
        await createCcAdminRole({
          code,
          displayName: form.displayName.trim(),
          description: form.description?.trim() || undefined,
          screenPermissions: permissionsObjectToPayload(form.screenPermissions),
          active: form.active !== false,
        });
        toast.success("Role created successfully");
      } else if (editingRole?.id) {
        const body = {
          displayName: form.displayName.trim(),
          description: form.description?.trim() || undefined,
        };
        if (!editingRole.systemRole) {
          body.screenPermissions = permissionsObjectToPayload(form.screenPermissions);
          if (form.active !== editingRole.active) body.active = form.active;
        }
        await patchCcAdminRole(editingRole.id, body);
        toast.success("Role updated successfully");
      }
      setFormOpen(false);
      setEditingRole(null);
      fetchRoles();
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, "Failed to save role")
          : "Failed to save role"
      );
    } finally {
      setSaving(false);
    }
  };

  const canDeactivate = (role) =>
    !role?.systemRole && (role?.userCount ?? 0) === 0 && role?.active !== false;

  const openDelete = (role) => {
    if (!canDeactivate(role)) return;
    setRoleToDelete(role);
    setDeleteOpen(true);
  };

  const handleDeactivate = async () => {
    if (!roleToDelete?.id) return;
    setSaving(true);
    try {
      await deactivateCcAdminRole(roleToDelete.id);
      toast.success("Role deactivated successfully");
      setDeleteOpen(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error) {
      const envelope = error?.envelope ?? error?.response?.data;
      toast.error(
        envelope != null
          ? getCallCenterApiErrorMessage(envelope, "Failed to deactivate role")
          : "Failed to deactivate role"
      );
    } finally {
      setSaving(false);
    }
  };

  const tableHeaders = [
    translations[langMode].roleName || "Role",
    "Code",
    translations[langMode].description,
    "Users",
    "Permissions",
    "Status",
    ...(showRoleActionColumn ? [translations[langMode].Action] : []),
  ];

  return (
    <>
      <div
        className={`w-full h-full ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}
      >
        <div className="flex flex-wrap justify-between items-center">
          <h4
            className={`uppercase ml-5 inline-flex items-center gap-2 mt-5 font-semibold whitespace-nowrap text-gray-800 ${
              theme === "dark" ? "dark-mode " : theme === "high-contrast" ? "high-contrast " : ""
            }`}
          >
            {translations[langMode].roleManagement}
          </h4>
          {canCreateRole && (
            <CommonButton variant="primary" onClick={openCreate} className="mr-5 mt-5">
              {translations[langMode].createRole}
            </CommonButton>
          )}
        </div>

        <div
          className={`${
            theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
          } w-full sm:w-auto mt-3 px-4 bg-white sm:rounded-lg`}
        >
          <div className="flex flex-wrap items-end gap-3 mt-4 pb-2 border-b border-gray-100">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Sort by</label>
              <select
                className="border border-gray-300 rounded-lg text-sm px-2 py-2 min-w-[140px] bg-white"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {SORT_BY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-500">Direction</label>
              <select
                className="border border-gray-300 rounded-lg text-sm px-2 py-2 min-w-[120px] bg-white"
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {SORT_DIR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => {
                  setIncludeInactive(e.target.checked);
                  setCurrentPage(0);
                }}
              />
              Show inactive roles
            </label>
          </div>

          <div className="flex justify-between mt-3">
            <p className="text-sm text-gray-400 flex items-center">
              {`${startIndex + 1}-${Math.min(endIndex, totalItems)} of ${totalItems} ${translations[langMode].items || "items"}`}
              {isRtl ? (
                <IoIosArrowForward
                  onClick={() => canGoPrev && setCurrentPage((p) => p - 1)}
                  className={`cursor-pointer ml-2 ${!canGoPrev ? "opacity-50" : ""}`}
                />
              ) : (
                <IoIosArrowBack
                  onClick={() => canGoPrev && setCurrentPage((p) => p - 1)}
                  className={`cursor-pointer ml-2 ${!canGoPrev ? "opacity-50" : ""}`}
                />
              )}
              {isRtl ? (
                <IoIosArrowBack
                  onClick={() => canGoNext && setCurrentPage((p) => p + 1)}
                  className={`cursor-pointer ${!canGoNext ? "opacity-50" : ""}`}
                />
              ) : (
                <IoIosArrowForward
                  onClick={() => canGoNext && setCurrentPage((p) => p + 1)}
                  className={`cursor-pointer ${!canGoNext ? "opacity-50" : ""}`}
                />
              )}
            </p>
            <div className="text-sm text-gray-400 flex items-center gap-2">
              <label htmlFor="rolePageSize">{translations[langMode].rows || "Rows"}:</label>
              <select
                id="rolePageSize"
                className="bg-gray-50 border border-gray-300 text-sm rounded-lg p-2"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Math.min(200, Math.max(1, parseInt(e.target.value, 10) || 20)));
                  setCurrentPage(0);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto my-5">
            <div className="relative max-h-[450px] overflow-y-auto rounded-xl shadow-lg border border-gray-100">
              <table className="min-w-full text-sm text-left text-gray-700">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
                  <tr>
                    {tableHeaders.map((header, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="px-6 py-3 text-xs font-semibold tracking-wider uppercase text-gray-600"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                {loading && <Spinner />}
                <tbody className="bg-white divide-y divide-gray-100">
                  {roleList.length > 0 ? (
                    roleList.map((role) => (
                      <tr key={role.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap font-medium">
                          {role.displayName}
                          {role.systemRole && (
                            <span className="ml-2 text-xs text-gray-500 font-normal">(built-in)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{role.code}</code>
                        </td>
                        <td className="px-6 py-4 max-w-xs truncate" title={role.description || ""}>
                          {role.description || "—"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{role.userCount ?? 0}</td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-gray-600">
                            {(role.screenPermissions || []).length} screen
                            {(role.screenPermissions || []).length !== 1 ? "s" : ""}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              role.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            }`}
                          >
                            {role.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        {showRoleActionColumn && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {canUpdateRole && (
                                <button
                                  type="button"
                                  onClick={() => openEdit(role)}
                                  className="p-2 rounded-lg text-blue-600 hover:bg-blue-50"
                                  title={translations[langMode].updateRole}
                                >
                                  <MdOutlineEdit size={20} />
                                </button>
                              )}
                              {canDeleteRole && (
                                <button
                                  type="button"
                                  onClick={() => openDelete(role)}
                                  disabled={!canDeactivate(role)}
                                  title={
                                    role.systemRole
                                      ? "Built-in roles cannot be deactivated"
                                      : (role.userCount ?? 0) > 0
                                        ? "Role is assigned to users"
                                        : translations[langMode].deleteRole
                                  }
                                  className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <MdOutlineDelete size={20} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={tableHeaders.length} className="text-center py-6 text-gray-500">
                        {translations[langMode].noRolesFound || "No roles found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CcRoleFormModal
        open={formOpen}
        mode={formMode}
        role={editingRole}
        onClose={() => {
          setFormOpen(false);
          setEditingRole(null);
        }}
        onSave={handleSaveForm}
        loading={saving}
        theme={theme}
      />

      <Transition.Root show={deleteOpen && !!roleToDelete} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setDeleteOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-[2px]" />
          </Transition.Child>
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Dialog.Panel className="relative rounded-2xl shadow-xl sm:max-w-md w-full bg-white p-6">
                <Dialog.Title className="text-lg font-semibold mb-2">Deactivate role</Dialog.Title>
                <p className="text-gray-700">
                  Deactivate <strong>{roleToDelete?.displayName}</strong> ({roleToDelete?.code})? Users cannot be
                  assigned an inactive role.
                </p>
                <div className="flex justify-end gap-3 mt-6">
                  <CommonButton variant="danger" type="button" onClick={() => setDeleteOpen(false)}>
                    Cancel
                  </CommonButton>
                  <CommonButton variant="danger" onClick={handleDeactivate} disabled={saving}>
                    Deactivate
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default RoleManagement;
